/**
 * 影子跟读练习页面
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ShadowMaterial,
  VideoBlob,
  getMaterial,
  getVideoBlob,
  updateProgress,
} from '../../../db/shadowDb';
import { useRecorder, RecordingResult } from '../../hooks/useRecorder';
import { VideoPlayer, VideoPlayerRef, PlaybackControls } from '../components/VideoPlayer';
import { SentenceNav } from '../components/SentenceNav';
import { WaveformCompare } from '../components/WaveformCompare';
import '../styles/shadowReading.css';

export function ShadowPracticePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // 数据状态
  const [material, setMaterial] = useState<ShadowMaterial | null>(null);
  const [videoBlob, setVideoBlob] = useState<VideoBlob | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 练习状态
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [userRecordingUrl, setUserRecordingUrl] = useState<string | null>(null);
  const [originalAudioBlob, setOriginalAudioBlob] = useState<Blob | null>(null);
  // 整体听读模式：无字幕从头播到尾
  const [isListenMode, setIsListenMode] = useState(false);
  // 字幕显示开关
  const [showSubtitle, setShowSubtitle] = useState(true);

  // Refs
  const videoPlayerRef = useRef<VideoPlayerRef>(null);
  // 用于在异步回调中访问最新状态（避免 stale closure）
  const materialRef = useRef<ShadowMaterial | null>(null);
  const currentIndexRef = useRef(0);

  // 同步 ref 与 state
  useEffect(() => {
    materialRef.current = material;
  }, [material]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // 录音完成回调（使用 ref 避免 stale closure）
  const handleRecordingCompleteRef = useRef(async (result: RecordingResult) => {
    console.log('[Recording] Complete, setting userRecordingUrl');
    setUserRecordingUrl(result.dataUrl);

    const mat = materialRef.current;
    const idx = currentIndexRef.current;

    // 提取原音用于波形对比（先提取，再更新进度）
    const segment = mat?.subtitles[idx];
    if (segment && videoPlayerRef.current) {
      try {
        console.log('[Recording] Extracting original audio...');
        const audioBlob = await videoPlayerRef.current.extractSegmentAudio(segment);
        if (audioBlob) {
          console.log('[Recording] Setting originalAudioBlob');
          setOriginalAudioBlob(audioBlob);
        }
      } catch (err) {
        console.error('Failed to extract segment audio:', err);
      }
    }

    // 更新练习进度（放到最后，避免状态更新干扰）
    if (mat) {
      await updateProgress(mat.id, idx);
      const updated = await getMaterial(mat.id);
      if (updated) {
        console.log('[Recording] Updating material');
        setMaterial(updated);
      }
    }
  });

  // 录音 Hook（使用 ref 回调避免 stale closure）
  const {
    state: recorderState,
    startRecording,
    stopRecording,
    isRecording,
  } = useRecorder({
    onRecordingComplete: (result) => handleRecordingCompleteRef.current(result),
    onError: (err) => console.error('Recording error:', err),
    maxDurationMs: 60000, // 最长 60 秒
  });

  // 加载素材
  useEffect(() => {
    if (!id) return;

    let cancelled = false;

    (async () => {
      try {
        const mat = await getMaterial(id);
        if (!mat) {
          throw new Error('素材不存在');
        }
        if (cancelled) return;
        setMaterial(mat);

        // 加载视频
        if (mat.videoBlobId) {
          const blob = await getVideoBlob(mat.videoBlobId);
          if (!cancelled && blob) {
            setVideoBlob(blob);
          }
        }

        // 恢复上次练习位置
        if (mat.progress.practicedSentences.length > 0) {
          const maxIndex = Math.max(...mat.progress.practicedSentences);
          const nextIndex = Math.min(maxIndex + 1, mat.subtitles.length - 1);
          setCurrentIndex(nextIndex);
        }
      } catch (err) {
        if (!cancelled) {
          setError((err as Error).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id]);

  // 当前字幕
  const currentSegment = useMemo(() => {
    return material?.subtitles[currentIndex];
  }, [material, currentIndex]);

  // 进度百分比
  const progressPercent = useMemo(() => {
    if (!material) return 0;
    const practiced = material.progress.practicedSentences.length;
    const total = material.subtitles.length;
    return total > 0 ? (practiced / total) * 100 : 0;
  }, [material]);

  // 导航到指定句子（并自动播放）
  const navigateToSentence = useCallback((index: number) => {
    setCurrentIndex(index);
    setUserRecordingUrl(null);
    setOriginalAudioBlob(null);

    // 自动播放对应片段
    const segment = material?.subtitles[index];
    if (segment && videoPlayerRef.current) {
      videoPlayerRef.current.playSegment(segment);
    }
  }, [material]);

  // 上一句
  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      navigateToSentence(currentIndex - 1);
    }
  }, [currentIndex, navigateToSentence]);

  // 下一句
  const handleNext = useCallback(() => {
    if (material && currentIndex < material.subtitles.length - 1) {
      navigateToSentence(currentIndex + 1);
    }
  }, [material, currentIndex, navigateToSentence]);

  // 播放/暂停当前句子
  const handlePlayCurrent = useCallback(() => {
    if (!currentSegment || !videoPlayerRef.current) return;

    if (isPlaying) {
      // 暂停时清除片段边界，这样用户可以自由控制
      videoPlayerRef.current.clearSegmentEnd();
      videoPlayerRef.current.pause();
    } else {
      // 播放当前句子片段
      videoPlayerRef.current.playSegment(currentSegment);
    }
  }, [currentSegment, isPlaying]);

  // 重播当前句（解决字幕同步问题）
  const handleReplayCurrent = useCallback(() => {
    if (!currentSegment || !videoPlayerRef.current) return;
    // 重新播放当前句子片段（会自动在句尾暂停）
    videoPlayerRef.current.playSegment(currentSegment);
  }, [currentSegment]);

  // 整体听读模式：无字幕从头播到尾
  const handleListenMode = useCallback(() => {
    if (!videoPlayerRef.current) return;
    // 进入听读模式
    setIsListenMode(true);
    // 清除片段结束时间，允许播放到视频结束
    videoPlayerRef.current.clearSegmentEnd();
    videoPlayerRef.current.seekTo(0);
    videoPlayerRef.current.play();
  }, []);

  // 退出听读模式
  const handleExitListenMode = useCallback(() => {
    setIsListenMode(false);
    if (videoPlayerRef.current) {
      videoPlayerRef.current.pause();
    }
  }, []);

  // 片段播放结束回调
  const handleSegmentEnd = useCallback(async () => {
    // 播放结束，不做特殊处理
  }, []);

  // 录音按钮点击
  const handleRecordClick = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      setUserRecordingUrl(null);
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording]);

  // 标记为熟练（跳到下一句）
  const handleMarkProficient = useCallback(async () => {
    if (material && currentIndex < material.subtitles.length - 1) {
      // 更新进度
      await updateProgress(material.id, currentIndex);
      const updated = await getMaterial(material.id);
      if (updated) {
        setMaterial(updated);
      }
      handleNext();
    }
  }, [material, currentIndex, handleNext]);

  // 返回
  const handleBack = useCallback(() => {
    navigate('/speaking/shadow');
  }, [navigate]);

  // 键盘快捷键: A=上一句, S=播放当前句, D=下一句, R=录音
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // 忽略输入框中的按键
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return;
      }

      switch (e.key.toLowerCase()) {
        case 'a':
          // A = 上一句
          handlePrev();
          break;
        case 's':
          // S = 播放当前句
          handlePlayCurrent();
          break;
        case 'd':
          // D = 下一句
          if (userRecordingUrl) {
            handleMarkProficient();
          } else {
            handleNext();
          }
          break;
        case 'r':
          // R = 录音（开始/停止/重录）
          handleRecordClick();
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [userRecordingUrl, handleRecordClick, handlePlayCurrent, handlePrev, handleNext, handleMarkProficient]);

  if (loading) {
    return (
      <div className="shadow-practice-page">
        <div className="shadow-loading">
          <div className="loading-spinner"><span></span></div>
          <p>加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !material) {
    return (
      <div className="shadow-practice-page">
        <div className="shadow-header">
          <button type="button" className="back-btn" onClick={handleBack}>←</button>
          <h2 className="page-title">错误</h2>
        </div>
        <div className="shadow-content">
          <p>{error || '素材加载失败'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shadow-practice-page">
      {/* 视频区域（占据主要空间） */}
      <div className="video-area">
        {/* 头部：覆盖在视频上 */}
        <div className="practice-header">
          <button type="button" className="back-btn" onClick={handleBack}>←</button>
          <div className="header-info">
            <h3 className="header-title">{material.title}</h3>
            <p className="header-subtitle">
              {currentIndex + 1}/{material.subtitles.length} 句
            </p>
          </div>
        </div>

        {/* 视频播放器 */}
        {videoBlob && (
          <VideoPlayer
            ref={videoPlayerRef}
            videoBlob={videoBlob}
            currentSegment={currentSegment}
            onPlayStateChange={setIsPlaying}
            onSegmentEnd={handleSegmentEnd}
            onVideoEnd={handleExitListenMode}
          />
        )}

        {/* 字幕：覆盖在视频区域底部（黑边位置），可手动关闭 */}
        {currentSegment && showSubtitle && !isListenMode && (
          <div className="subtitle-overlay">
            <p className="subtitle-text">{currentSegment.text}</p>
          </div>
        )}

        {/* 进度条：视频区域最底部 */}
        <div className="practice-progress">
          <div className="progress-fill" style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      {/* 底部控制面板 */}
      <div className="control-panel">
        {/* 句子导航 - 听读模式下隐藏 */}
        {!isListenMode && (
          <SentenceNav
            subtitles={material.subtitles}
            currentIndex={currentIndex}
            progress={material.progress}
            onNavigate={navigateToSentence}
          />
        )}

        {/* 听读模式提示 */}
        {isListenMode && (
          <div className="listen-mode-hint">
            👂 整体听读中... 点击 ⏹ 退出
          </div>
        )}

        {/* 主控制区：播放按钮左 + 录音按钮右 */}
        <div className="control-main">
          {/* 播放控制 */}
          <PlaybackControls
            onPrev={handlePrev}
            onPlayCurrent={handlePlayCurrent}
            onNext={handleNext}
            onReplay={handleReplayCurrent}
            onListenMode={isListenMode ? handleExitListenMode : handleListenMode}
            onToggleSubtitle={() => setShowSubtitle(!showSubtitle)}
            isPlaying={isPlaying}
            hasPrev={currentIndex > 0}
            hasNext={currentIndex < material.subtitles.length - 1}
            isListenMode={isListenMode}
            showSubtitle={showSubtitle}
          />

          {/* 中间空白区域 */}
          <div className="control-spacer" />

          {/* 录音按钮 - 听读模式下隐藏 */}
          {!isListenMode && (
            <div className="record-inline">
              <button
                type="button"
                className={`record-btn ${isRecording ? 'recording' : ''}`}
                onClick={handleRecordClick}
              >
                {isRecording ? `⏹ ${recorderState.elapsedSeconds}s` : '🎤 跟读'}
              </button>
            </div>
          )}
        </div>

        {/* 波形对比 - 录音完成后显示，听读模式下隐藏 */}
        {!isListenMode && (originalAudioBlob || userRecordingUrl) && (
          <WaveformCompare
            originalAudio={originalAudioBlob || undefined}
            userAudioDataUrl={userRecordingUrl || undefined}
          />
        )}
      </div>
    </div>
  );
}
